import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type { FiatCurrency } from '__generated__/globalTypes';
import { Cryptocurrency, Currency } from '__generated__/globalTypes';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useIntlContext } from 'contexts/intl';
import { withFragments } from 'lib/gql';
import { SOL_DECIMAL_PLACES } from 'lib/lamport';
import type { MonetaryAmountParams } from 'lib/monetaryAmount';
import { MonetaryAmount } from 'lib/monetaryAmount';
import { ETH_DECIMAL_PLACES } from 'lib/wei';

import type { useAmountWithConversion_monetaryAmount } from './__generated__/useAmountWithConversion.graphql';
import { useMonetaryAmount } from './useMonetaryAmount';

export type Props = {
  monetaryAmount: MonetaryAmountParams | MonetaryAmount | null | undefined;
  primaryCurrency?: Currency;
  hideFractionDigits?: boolean;
  hideSymbol?: boolean;
  useGrouping?: boolean;
  forceFiatCurrency?: FiatCurrency;
  forceCryptocurrency?: Cryptocurrency;
};

export const useAmountWithConversion = withFragments(
  ({
    monetaryAmount,
    primaryCurrency,
    hideFractionDigits,
    hideSymbol,
    useGrouping = true,
    forceFiatCurrency,
    forceCryptocurrency,
  }: Props) => {
    const { formatNumber, formatWei, formatLamport } = useIntlContext();
    const { toMonetaryAmount } = useMonetaryAmount();
    const {
      cryptocurrency: userCryptocurrency,
      fiatCurrency: userFiatCurrency,
      currency: userCurrency,
      walletPreferences: { onlyShowFiatCurrency },
    } = useCurrentUserContext();

    const cryptocurrency = forceCryptocurrency || userCryptocurrency;
    const fiatCurrency = forceFiatCurrency || userFiatCurrency;

    const fullMonetaryAmount =
      monetaryAmount instanceof MonetaryAmount
        ? monetaryAmount
        : toMonetaryAmount(monetaryAmount);

    const fiatAmount = fullMonetaryAmount.toMajorUnit(fiatCurrency);
    const fiatFormatted = formatNumber(fiatAmount, {
      style: 'currency',
      currency: fiatCurrency,
      ...(hideFractionDigits ? { maximumFractionDigits: 0 } : {}),
    });

    const cryptoAmount = (() => {
      switch (cryptocurrency) {
        case Cryptocurrency.LAMPORT:
          return fullMonetaryAmount.lamport;
        case Cryptocurrency.WEI:
        default:
          return fullMonetaryAmount.wei;
      }
    })();
    const cryptoFormatted = (() => {
      switch (cryptocurrency) {
        case Cryptocurrency.WEI:
          return formatWei(
            cryptoAmount,
            undefined,
            {
              maximumFractionDigits: ETH_DECIMAL_PLACES,
              useGrouping,
            },
            { hideSymbol }
          );
        case Cryptocurrency.LAMPORT:
        default:
          return formatLamport(
            cryptoAmount,
            undefined,
            {
              maximumFractionDigits: SOL_DECIMAL_PLACES,
              useGrouping,
            },
            { hideSymbol }
          );
      }
    })();

    const actualPrimaryCurrency = primaryCurrency || userCurrency;

    const exponent =
      actualPrimaryCurrency === Currency.ETH ? fiatFormatted : cryptoFormatted;

    return {
      main:
        actualPrimaryCurrency === Currency.ETH
          ? cryptoFormatted
          : fiatFormatted,
      exponent:
        primaryCurrency === Currency.ETH || !onlyShowFiatCurrency
          ? exponent
          : null,
      cryptoAmount,
    };
  },
  {
    monetaryAmount: gql`
      fragment useAmountWithConversion_monetaryAmount on MonetaryAmount {
        ...useMonetaryAmount_monetaryAmount
      }
      ${useMonetaryAmount.fragments.monetaryAmount}
    ` as TypedDocumentNode<useAmountWithConversion_monetaryAmount>,
  }
);
